import dayjs from "dayjs";
import { LanguageNameSpaces } from "@shared/types";
import { TranslationKeys } from "@shared/common/i18n/types.ts";
import {
  FinanceCategory,
  FinanceCurrency,
  FinanceDirection,
  FinanceGranularity,
  FinanceSource,
} from "features/finance/types";

type AdminKey = TranslationKeys<LanguageNameSpaces.ADMIN>;

export const SUPERUSER_PERMISSION_LEVEL = 1;

export const FINANCE_DATE_FORMAT = "YYYY-MM-DD";
export const FINANCE_MONTH_FORMAT = "YYYY-MM";
export const DAILY_GRANULARITY_MAX_DAYS = 62;

export const GRANULARITY_TO_PERIOD_FORMAT: Record<FinanceGranularity, string> =
  {
    [FinanceGranularity.MONTH]: FINANCE_MONTH_FORMAT,
    [FinanceGranularity.DAY]: FINANCE_DATE_FORMAT,
  };

export const GRANULARITY_TO_LABEL_FORMAT: Record<FinanceGranularity, string> = {
  [FinanceGranularity.MONTH]: "MMM YYYY",
  [FinanceGranularity.DAY]: "D MMM",
};
export const FINANCE_ENTRIES_LIMIT = 1000;

export const getDefaultFinanceRange = (): [dayjs.Dayjs, dayjs.Dayjs] => [
  dayjs().subtract(11, "month").startOf("month"),
  dayjs().startOf("day"),
];

export const FINANCE_CURRENCIES = [
  FinanceCurrency.USD,
  FinanceCurrency.ILS,
] as const;

export const CURRENCY_SYMBOL: Record<string, string> = {
  [FinanceCurrency.USD]: "$",
  [FinanceCurrency.ILS]: "₪",
};

export const DIRECTION_TO_LABEL: Record<FinanceDirection, AdminKey> = {
  [FinanceDirection.INCOME]: "finance.directions.income",
  [FinanceDirection.EXPENSE]: "finance.directions.expense",
};

export const SOURCE_TO_LABEL: Record<FinanceSource, AdminKey> = {
  [FinanceSource.ISRACARD]: "finance.sources.isracard",
  [FinanceSource.CAL]: "finance.sources.cal",
  [FinanceSource.MIZRAHI]: "finance.sources.mizrahi",
  [FinanceSource.PAYPAL]: "finance.sources.paypal",
  [FinanceSource.COINPAYMENTS]: "finance.sources.coinpayments",
  [FinanceSource.AFFILIATION]: "finance.sources.affiliation",
  [FinanceSource.APPLE]: "finance.sources.apple",
  [FinanceSource.GOOGLE]: "finance.sources.google",
  [FinanceSource.WHOP]: "finance.sources.whop",
  [FinanceSource.MANUAL]: "finance.sources.manual",
  [FinanceSource.EXCHANGE]: "finance.sources.exchange",
};

export const CATEGORY_TO_LABEL: Record<FinanceCategory, AdminKey> = {
  [FinanceCategory.PAYMENT_FEES]: "finance.categories.payment_fees",
  [FinanceCategory.AFFILIATION]: "finance.categories.affiliation",
  [FinanceCategory.BANK_FEES]: "finance.categories.bank_fees",
  [FinanceCategory.CONVERSION_FEES]: "finance.categories.conversion_fees",
  [FinanceCategory.CLOUD]: "finance.categories.cloud",
  [FinanceCategory.SALARIES]: "finance.categories.salaries",
  [FinanceCategory.BANK_TRANSFERS]: "finance.categories.bank_transfers",
  [FinanceCategory.CARD_EXPENSES]: "finance.categories.card_expenses",
  [FinanceCategory.SOFTWARE]: "finance.categories.software",
  [FinanceCategory.MARKETING]: "finance.categories.marketing",
  [FinanceCategory.TAXES]: "finance.categories.taxes",
  [FinanceCategory.OTHER]: "finance.categories.other",
  [FinanceCategory.INTERNAL_TRANSFER]: "finance.categories.internal_transfer",
  [FinanceCategory.PROFESSIONAL_SERVICES]:
    "finance.categories.professional_services",
  [FinanceCategory.SUBSCRIPTIONS]: "finance.categories.subscriptions",
  [FinanceCategory.EXCHANGE_COMMISSION]:
    "finance.categories.exchange_commission",
  [FinanceCategory.REFUNDS]: "finance.categories.refunds",
};

export const ALL_SOURCES = Object.values(FinanceSource).filter(
  (value): value is FinanceSource => typeof value === "number"
);

export const EXPENSE_CATEGORIES = [
  FinanceCategory.PAYMENT_FEES,
  FinanceCategory.AFFILIATION,
  FinanceCategory.BANK_FEES,
  FinanceCategory.CONVERSION_FEES,
  FinanceCategory.CLOUD,
  FinanceCategory.SALARIES,
  FinanceCategory.BANK_TRANSFERS,
  FinanceCategory.CARD_EXPENSES,
  FinanceCategory.SOFTWARE,
  FinanceCategory.MARKETING,
  FinanceCategory.TAXES,
  FinanceCategory.PROFESSIONAL_SERVICES,
  FinanceCategory.OTHER,
];

export const INCOME_CATEGORIES = [
  FinanceCategory.SUBSCRIPTIONS,
  FinanceCategory.EXCHANGE_COMMISSION,
];

export const SYNCED_ONLY_CATEGORIES = [FinanceCategory.REFUNDS];

export const NEUTRAL_CATEGORIES = [FinanceCategory.INTERNAL_TRANSFER];

export const ALL_CATEGORIES = [
  ...INCOME_CATEGORIES,
  ...SYNCED_ONLY_CATEGORIES,
  ...EXPENSE_CATEGORIES,
  ...NEUTRAL_CATEGORIES,
];

export const STATEMENT_SOURCES = [
  FinanceSource.ISRACARD,
  FinanceSource.CAL,
  FinanceSource.MIZRAHI,
];

export const DELETABLE_SOURCES = new Set<FinanceSource>([
  FinanceSource.ISRACARD,
  FinanceSource.CAL,
  FinanceSource.MIZRAHI,
  FinanceSource.MANUAL,
]);

export const STATEMENT_FILE_ACCEPT = ".xlsx,.xls,.csv";
